/** Adapts a server-rendered form to the popup lifecycle without an iframe. */
interface InlineContentCallbacks {
	onRendered: () => void;
	onSubmitted: () => void;
	onPageChanged: (currentPage: number) => void;
	onClose: () => void;
	onRedirect: (url: string) => void;
	onResize: () => void;
	isOpen: () => boolean;
}

interface NativeSubmissionData {
	form?: Element;
	submissionResult?: {
		data?: { gp_popups_redirect?: string };
	};
}

interface NativeUtilities {
	addAsyncFilter: (
		event: string,
		callback: (data: NativeSubmissionData) => Promise<NativeSubmissionData>
	) => void;
}

const nativeSubmissionHandlers = new Set<
	(data: NativeSubmissionData) => void
>();
const connectedUtilities = new WeakSet<NativeUtilities>();

function connectNativeSubmissionEvents(): void {
	const utilities = (window.gform as unknown as { utils?: NativeUtilities })
		?.utils;
	if (!utilities?.addAsyncFilter || connectedUtilities.has(utilities)) {
		return;
	}
	// One shared filter allows destroyed popups to unsubscribe without touching
	// another add-on's filters or retaining their detached form DOM.
	utilities.addAsyncFilter(
		'gform/ajax/post_ajax_submission',
		async (data) => {
			nativeSubmissionHandlers.forEach((handler) => handler(data));
			return data;
		}
	);
	connectedUtilities.add(utilities);
}

/** Hide an early-rendered banner when its client-side eligibility check fails. */
export function hideInitialPopup(feedId: number): void {
	const shell = document.querySelector<HTMLElement>(
		`[data-gp-popup-initial="${feedId}"]`
	);
	if (!shell) {
		return;
	}
	shell.hidden = true;
	shell.classList.remove('gp-popup--open');
	const position = shell.classList.contains('gp-popup--banner-top')
		? 'top'
		: 'bottom';
	document.body.classList.remove(`gp-popup-banner-${position}-open`);
	document.body.style.removeProperty('--gp-popup-banner-height');
}

export class InlineContent {
	private hasSubmitted = false;
	private mutationObserver: MutationObserver;
	private resizeObserver: ResizeObserver | null = null;
	private dialog: HTMLElement | null = null;
	private previousFocus: HTMLElement | null = null;
	private readonly namespace: string;
	private forms = new WeakSet<Element>();
	private legacyPostRenderForms = new Set<number>();

	public static find(feedId: number, formId: number): HTMLElement | null {
		const root = document.getElementById(`gp-popup-inline-${feedId}`);
		if (!root) {
			return null;
		}

		// The server also checks this before rendering. This catches forms added
		// later by page builders before the popup is first opened.
		if (
			formId &&
			Array.from(
				document.querySelectorAll(
					`[id="gform_wrapper_${formId}"], [id="gform_${formId}"]`
				)
			).some((form) => !root.contains(form))
		) {
			root.remove();
			return null;
		}

		return root;
	}

	constructor(
		private root: HTMLElement,
		private feedId: number,
		private formId: number,
		private callbacks: InlineContentCallbacks,
		private trapFocus: boolean
	) {
		this.namespace = `.gpPopupInline${feedId}`;
		this.rememberForms();
		nativeSubmissionHandlers.add(this.handleNativeSubmission);
		connectNativeSubmissionEvents();
		this.root.addEventListener('click', this.handleClick);
		document.addEventListener('gform/post_render', this.handlePostRender);
		document.addEventListener(
			'gform/ajax/post_page_change',
			this.handlePageChange
		);
		document.addEventListener(
			'gp_popup_inline_redirect',
			this.handleRedirect
		);
		document.addEventListener('keydown', this.handleKeydown);

		if (typeof jQuery !== 'undefined') {
			jQuery(document)
				.on(
					`gform_post_render${this.namespace}`,
					this.handleLegacyPostRender
				)
				.on(
					`gform_confirmation_loaded${this.namespace}`,
					(_event, formId) => {
						if (Number(formId) === this.formId) {
							this.checkConfirmation();
						}
					}
				)
				.on(
					`gform_page_loaded${this.namespace}`,
					(_event, formId, currentPage) => {
						if (Number(formId) === this.formId) {
							this.callbacks.onPageChanged(Number(currentPage));
						}
					}
				);
		}

		// Native AJAX can replace the form without emitting the legacy jQuery
		// confirmation event. Observe only this popup's own content.
		this.mutationObserver = new MutationObserver(() => {
			this.rememberForms();
			this.checkConfirmation();
		});
		this.mutationObserver.observe(root, { childList: true, subtree: true });

		if (typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(() => {
				if (this.callbacks.isOpen()) {
					this.callbacks.onResize();
				}
			});
			this.resizeObserver.observe(root);
		}
	}

	public mount(content: HTMLElement, dialog: HTMLElement): void {
		this.dialog = dialog;
		if (this.root.parentElement !== content) {
			content.appendChild(this.root);
		}
		this.root.hidden = false;
		this.root.removeAttribute('aria-hidden');
	}

	public focus(): void {
		this.previousFocus = this.root.ownerDocument
			.activeElement as HTMLElement | null;
		const field = this.root.querySelector<HTMLElement>(
			'.gform_wrapper input:not([type="hidden"]):not([disabled]), .gform_wrapper textarea:not([disabled]), .gform_wrapper select:not([disabled])'
		);
		const fallback = this.trapFocus
			? this.dialog?.querySelector<HTMLElement>('button')
			: null;
		(field || fallback)?.focus();
	}

	public hide(): void {
		if (this.dialog?.contains(this.root.ownerDocument.activeElement)) {
			this.previousFocus?.focus();
		}
	}

	public destroy(): void {
		nativeSubmissionHandlers.delete(this.handleNativeSubmission);
		this.mutationObserver.disconnect();
		this.resizeObserver?.disconnect();
		this.root.removeEventListener('click', this.handleClick);
		document.removeEventListener(
			'gform/post_render',
			this.handlePostRender
		);
		document.removeEventListener(
			'gform/ajax/post_page_change',
			this.handlePageChange
		);
		document.removeEventListener(
			'gp_popup_inline_redirect',
			this.handleRedirect
		);
		document.removeEventListener('keydown', this.handleKeydown);
		if (typeof jQuery !== 'undefined') {
			jQuery(document).off(this.namespace);
		}
	}

	private rendered(formId: number): void {
		if (formId !== this.formId) {
			return;
		}
		this.rememberForms();
		connectNativeSubmissionEvents();
		this.checkConfirmation();
		if (
			!this.hasSubmitted &&
			this.root.querySelector(
				'.gform_validation_errors, .validation_error, .gfield_error'
			)
		) {
			this.updateConditionalVisibility(false);
		}
		this.callbacks.onRendered();
	}

	private rememberForms(): void {
		this.root
			.querySelectorAll(`[id="gform_${this.formId}"]`)
			.forEach((form) => {
				this.forms.add(form);
			});
	}

	private handleNativeSubmission = (data: NativeSubmissionData): void => {
		if (!data.form || !this.forms.has(data.form)) {
			return;
		}
		const redirect = data.submissionResult?.data?.gp_popups_redirect;
		if (typeof redirect === 'string' && redirect) {
			// PHP converts native redirects to an inert message, since native GF
			// sanitizes confirmation scripts. Route the URL through the same
			// cancelable popup event used by iframe and legacy AJAX submissions.
			this.hasSubmitted = true;
			this.callbacks.onRedirect(redirect);
		}
	};

	private checkConfirmation(): void {
		if (
			!this.hasSubmitted &&
			this.formId &&
			this.root.querySelector(
				`#gform_confirmation_wrapper_${this.formId}:not([data-gp-popup-redirect])`
			)
		) {
			this.hasSubmitted = true;
			this.updateConditionalVisibility(true);
			this.callbacks.onSubmitted();
		}
	}

	private updateConditionalVisibility(success: boolean): void {
		this.root
			.querySelectorAll<HTMLElement>('.gp-popups-conditional')
			.forEach((element) => {
				if (
					!success &&
					element.dataset.gpPopupsIncludeFailed !== 'true'
				) {
					return;
				}
				element.classList.toggle(
					'gp-popups-hidden',
					element.dataset.gpPopupsBehavior === 'hide'
				);
			});
	}

	private handleLegacyPostRender = (
		_event: unknown,
		formId: number | string
	): void => {
		const numericFormId = Number(formId);
		this.legacyPostRenderForms.add(numericFormId);
		void Promise.resolve().then(() =>
			this.legacyPostRenderForms.delete(numericFormId)
		);
		this.rendered(numericFormId);
	};

	private handlePostRender = (event: Event): void => {
		const formId = Number((event as CustomEvent).detail?.formId);
		if (this.legacyPostRenderForms.has(formId)) {
			return;
		}
		this.rendered(formId);
	};

	private handlePageChange = (event: Event): void => {
		const detail = (event as CustomEvent).detail;
		if (Number(detail?.formId) === this.formId) {
			this.callbacks.onPageChanged(Number(detail.pageNumber));
		}
	};

	private handleRedirect = (event: Event): void => {
		const detail = (event as CustomEvent).detail;
		if (
			Number(detail?.feedId) === this.feedId &&
			typeof detail.url === 'string'
		) {
			this.hasSubmitted = true;
			this.callbacks.onRedirect(detail.url);
		}
	};

	private handleClick = (event: MouseEvent): void => {
		const target = event.target as Element | null;
		if (target?.closest('[data-gp-popup-dismiss]')) {
			event.preventDefault();
			this.callbacks.onClose();
		}
	};

	private handleKeydown = (event: KeyboardEvent): void => {
		if (
			!this.trapFocus ||
			!this.callbacks.isOpen() ||
			event.key !== 'Tab' ||
			!this.dialog
		) {
			return;
		}
		const focusable = Array.from(
			this.dialog.querySelectorAll<HTMLElement>(
				'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		).filter((element) => element.getClientRects().length > 0);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (!first) {
			event.preventDefault();
			return;
		}
		if (
			event.shiftKey &&
			(this.root.ownerDocument.activeElement === first ||
				!this.dialog.contains(this.root.ownerDocument.activeElement))
		) {
			event.preventDefault();
			last.focus();
		} else if (
			!event.shiftKey &&
			(this.root.ownerDocument.activeElement === last ||
				!this.dialog.contains(this.root.ownerDocument.activeElement))
		) {
			event.preventDefault();
			first.focus();
		}
	};
}
